import { icons, RadixTabs, RawLink } from '@portal/components'
import React, { useCallback, useMemo } from 'react'

import { useCollectionFilterSettings, useQueryHistory } from '../../store'
import { buildQueryForCollection } from '../../utils'
import DataImportMenu from '../dataImporter/DataImportMenu'
import {
  useDQLEditorCollectionsContext,
  useDQLEditorContext,
} from '../dqlEditor/context'
import StrictModeToggle from './StrictModeToggle'

const { ArrowUpRight } = icons

// Collections starting with this prefix are considered internal/system collections
export const INTERNAL_COLLECTION_PREFIX = '__'

/**
 * The top bar for the DQLEditor
 *
 * This component controls the top bar of the DQLEditor, which includes the
 * breadcrumb, the tabs, and the data import menu. If the tab changes, the
 * current page is reset to 1 and the active row is cleared.
 */
export default function TopBar() {
  const {
    appId,
    activeView,
    setActiveView,
    appName,
    appCollections,
    appSettingsUrl,
    returnToUrl,
    setQuery,
    setActiveRow,
  } = useDQLEditorContext()
  const { setCurrentPage, collectionCounts } = useDQLEditorCollectionsContext()
  const { documentsLimit, hideEmptyCollections, hideInternalCollections } =
    useCollectionFilterSettings()
  const queries = useQueryHistory(appId || '')

  const onQueryCollection = useCallback(
    (collection: string) => {
      setQuery(buildQueryForCollection(collection, { limit: documentsLimit }))
    },
    [setQuery, documentsLimit],
  )

  const filteredCollections = useMemo(() => {
    let collections = appCollections

    if (hideInternalCollections) {
      collections = collections?.filter(
        (c) => !c.startsWith(INTERNAL_COLLECTION_PREFIX),
      )
    }

    if (hideEmptyCollections) {
      collections = collections?.filter((c) => collectionCounts[c] ?? 0 > 0)
    }

    return collections
  }, [
    appCollections,
    collectionCounts,
    hideEmptyCollections,
    hideInternalCollections,
  ])

  const handleViewChange = (view: string) => {
    if (view === 'collections' || view === 'editor') {
      // Prevents an empty query from being executed, and therefore
      // being stored in the history.
      if (view === 'collections' && filteredCollections?.length) {
        onQueryCollection(filteredCollections[0])
      } else if (view === 'editor') {
        const lastQuery = queries?.find((q) => q.activeView === 'editor')
        setQuery(lastQuery?.query || '', false)
      }

      // Sets active view
      setActiveView(view)

      // Resets the current page and active row
      setCurrentPage(1)
      setActiveRow(undefined)
    }
  }

  const breadcrumbSeparator = (
    <span aria-hidden="true" className="mx-2 text-foreground-subtle">
      /
    </span>
  )

  const renderBreadcrumb = () => {
    if (!appName || !appSettingsUrl || !returnToUrl) {
      return null
    }

    return (
      <div className="flex w-1/3 items-center">
        <RawLink
          to={appSettingsUrl}
          data-testid="returnToAppLink"
          title="Return to Portal"
          className="text-foreground-subtle hover:text-foreground-normal hover:underline"
        >
          {appName}
        </RawLink>

        {breadcrumbSeparator}

        <RawLink
          to={returnToUrl}
          data-testid="returnToCollectionsLink"
          title="Return to Collections"
          className="text-foreground-subtle hover:text-foreground-normal hover:underline"
        >
          Collections
        </RawLink>

        {breadcrumbSeparator}

        <span
          className="text-foreground-normal"
          data-testid="dqlEditorBreadcrumb"
        >
          DQL Editor
        </span>
      </div>
    )
  }

  return (
    <div className="flex h-12 flex-shrink-0 items-center justify-between border-b border-solid border-border-normal px-4">
      {renderBreadcrumb()}

      <div className="flex w-1/3 items-center justify-center">
        <RadixTabs
          defaultValue={activeView}
          value={activeView}
          onValueChange={handleViewChange}
          className="flex-shrink-0"
        >
          <RadixTabs.List className="w-full">
            <RadixTabs.Trigger value="collections" className="w-full">
              Collections
            </RadixTabs.Trigger>
            <RadixTabs.Trigger value="editor" className="w-full">
              DQL Editor
            </RadixTabs.Trigger>
          </RadixTabs.List>
        </RadixTabs>
      </div>

      <div className="flex w-1/3 items-center justify-end gap-3">
        <RawLink
          href="https://docs.ditto.live/dql"
          isBlank
          className="flex items-center gap-1"
        >
          Docs
          <ArrowUpRight className="size-4" />
        </RawLink>

        <DataImportMenu />

        <StrictModeToggle />
      </div>
    </div>
  )
}
